import AddRounded from "@mui/icons-material/Add";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import { useMemo } from "react";

import Button, { ButtonTypes } from "../../../../components/Button";
import { Carousel } from "../../../../components/Carousel";
import { ErrorState } from "../../../../components/States";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import { RecurringCard } from "./RecurringCard";
import {
  AddButton,
  EmptyCard,
  EmptyContent,
  EmptyDescription,
  EmptyIcon,
  EmptyText,
  EmptyTitle,
  Header,
  HeaderText,
  Section,
  SkeletonCard,
  SkeletonRow,
  Summary,
  SummaryValue,
  Title,
} from "./styles";
import { RecurringSectionProps } from "./types";
import { computeRecurringSummary, formatSignedAmount } from "./utils";

const SKELETON_KEYS = ["a", "b", "c"];

export const RecurringSection: React.FC<RecurringSectionProps> = ({
  recurringTransactions,
  isLoading,
  error,
  onAdd,
  onEdit,
  onDelete,
  onRetry,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletMin);
  const summary = useMemo(
    () => computeRecurringSummary(recurringTransactions),
    [recurringTransactions],
  );

  const hasItems = recurringTransactions.length > 0;
  const showError = error !== null && !hasItems;
  const showSkeleton = isLoading && !hasItems && !showError;
  const showEmpty = !isLoading && !hasItems && !showError;
  const addLabel = isCompact ? "Add" : "Add recurring";

  const summaryLine = (
    <Summary>
      {summary.activeCount} active · net{" "}
      <SummaryValue $variant={summary.monthlyVariant}>
        {formatSignedAmount(summary.monthlyNet, summary.monthlyVariant)}
      </SummaryValue>{" "}
      /month · next 14d{" "}
      <SummaryValue $variant={summary.next14dVariant}>
        {formatSignedAmount(summary.next14dSum, summary.next14dVariant)}
      </SummaryValue>
    </Summary>
  );

  const emptyState = (
    <EmptyCard>
      <EmptyContent>
        <EmptyIcon>
          <CalendarMonthOutlined />
        </EmptyIcon>
        <EmptyText>
          <EmptyTitle>Track what comes around again</EmptyTitle>
          <EmptyDescription>
            Subscriptions, salary, rent — anything that lands on a schedule. Add
            a rule and we'll surface the next charge and net monthly impact
            alongside the ledger.
          </EmptyDescription>
        </EmptyText>
      </EmptyContent>
      <Button
        type={ButtonTypes.primary}
        onClick={onAdd}
        aria-label="Add recurring rule"
      >
        <AddRounded fontSize="small" />
        Add recurring rule
      </Button>
    </EmptyCard>
  );

  const skeleton = (
    <SkeletonRow>
      {SKELETON_KEYS.map((key) => (
        <SkeletonCard key={key} />
      ))}
    </SkeletonRow>
  );

  const carousel = (
    <Carousel
      ariaLabelPrev="Previous recurring transactions"
      ariaLabelNext="Next recurring transactions"
    >
      {recurringTransactions.map((transaction) => (
        <RecurringCard
          key={transaction.id}
          transaction={transaction}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </Carousel>
  );

  const renderBody = () => {
    if (showError) {
      return <ErrorState message={error ?? ""} onRetry={onRetry} />;
    }
    if (showSkeleton) {
      return skeleton;
    }
    if (showEmpty) {
      return emptyState;
    }
    return carousel;
  };

  const header = (
    <Header>
      <HeaderText>
        <Title>Recurring</Title>
      </HeaderText>
      {!showEmpty && !showError && (
        <AddButton
          type={ButtonTypes.link}
          onClick={onAdd}
          aria-label="Add recurring"
        >
          <AddRounded />
          {addLabel}
        </AddButton>
      )}
    </Header>
  );

  let subtitle: React.ReactNode = summaryLine;
  if (showError) {
    subtitle = null;
  } else if (showEmpty) {
    subtitle = <Summary>No rules yet</Summary>;
  }

  return (
    <Section>
      {header}
      {subtitle}
      {renderBody()}
    </Section>
  );
};
